import React, { useEffect } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import MenuItem from "./MenuItem";
import { ArrowLeftIcon, CloseIcon, LogoIcon } from "./icons";
import { SidebarProps } from "./types";

/**
 * Sidebar menu — design-system component (Figma node 470:66847).
 * Holds all markup + styles; containers only pass data via props.
 *
 * With `drawer` (a phone) the same menu slides in over the page, always
 * expanded, with touch-sized rows, as in the admin panel.
 */
export const Sidebar: React.FC<SidebarProps> = ({
  sections,
  activeKey,
  opened = true,
  onToggle,
  onItemSelect,
  cta,
  footer,
  drawer,
  className,
}) => {
  const drawerOpen = Boolean(drawer?.open);
  const onClose = drawer?.onClose;
  const expanded = Boolean(drawer) || opened;

  // While the drawer is out the page under it stays put, and Escape puts
  // the drawer away.
  useEffect(() => {
    if (!drawerOpen || !onClose) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen, onClose]);

  // A picked item takes the drawer away with it.
  const select = (key: string) => {
    onItemSelect?.(key);
    onClose?.();
  };

  const nav = (
    <nav
      className={cs(
        styles.sidebar,
        {
          [styles.collapsed]: !expanded,
          [styles.drawer]: Boolean(drawer),
          [styles.drawerOpen]: drawerOpen,
        },
        className,
      )}
      aria-hidden={drawer && !drawerOpen ? true : undefined}
    >
      <div className={styles.inner}>
        <div className={styles.logoRow}>
          <LogoIcon className={styles.logo} />
          {drawer && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={drawer.onClose}
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        <div className={styles.list}>
          {sections.map((section, index) => (
            <React.Fragment key={section.key}>
              {index > 0 && <hr className={styles.divider} />}
              <div className={styles.section}>
                {expanded && section.title && (
                  <span className={styles.sectionTitle}>{section.title}</span>
                )}
                {section.items.map((item) => (
                  <MenuItem
                    key={item.key}
                    item={item}
                    active={!cta?.active && item.key === activeKey}
                    opened={expanded}
                    onSelect={select}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}

          {cta && (
            <MenuItem
              item={{
                key: "new-order",
                label: cta.label,
                icon: "new-order",
                onClick: cta.onClick,
              }}
              active={cta.active}
              opened={expanded}
              onSelect={() => onClose?.()}
              className={styles.cta}
            />
          )}
        </div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>

      {onToggle && !drawer && (
        <button
          type="button"
          className={styles.toggle}
          onClick={onToggle}
          aria-label={opened ? "Collapse menu" : "Expand menu"}
        >
          <ArrowLeftIcon />
        </button>
      )}
    </nav>
  );

  if (!drawer) return nav;

  return (
    <>
      <div
        className={cs(styles.backdrop, {
          [styles.backdropVisible]: drawerOpen,
        })}
        onClick={drawer.onClose}
        aria-hidden
      />
      {nav}
    </>
  );
};

export default Sidebar;
